import { Input, InputProps } from '../Input/Input.component';
import React, { ChangeEvent, FC, forwardRef, KeyboardEvent, useMemo, useState } from 'react';
import { Validator } from '@form/business/form.types';
import { useTranslation } from '@locales/useTranslation.hook';
import { plcDayjs } from '@utils/date';
import { Dayjs } from 'dayjs';

type Props = Omit<InputProps, 'type' | 'onChange' | 'onKeyPress' | 'pattern' | 'value' | 'onBlur'> & {
  onChange: (newValue: Dayjs | undefined) => void;
  value: Dayjs | undefined;
};

const SEPARATOR = ':';
const FORMAT = `HH${SEPARATOR}mm`;

function parseHourMinute(value: string | undefined) {
  let hour: number | undefined;
  let minute: number | undefined;
  if (value && value.length >= 3 && value.length <= 5) {
    const separatorPosition = value.indexOf(SEPARATOR);
    if (separatorPosition >= 0 && separatorPosition < value.length - 1) {
      hour = Number(value.substr(0, separatorPosition));
      minute = Number(value.substr(separatorPosition + 1, value.length - (separatorPosition + 1)));
    }
  }
  return { hour, minute };
}

const PlcTimePicker: FC<Props> = forwardRef(
  ({ value, onChange, validators, isHintVisible, inputMode = 'numeric', ...rest }, timePickerRef) => {
    const { t } = useTranslation(['common']);
    const [currentValue, setCurrentValue] = useState(value?.format(FORMAT));

    function handleOnKeyPress(event: KeyboardEvent<HTMLInputElement>) {
      // TODO : handle arrow up or down
      if (!event.key.match(/[0-9:]/)) {
        event.preventDefault();
      }
      if (currentValue?.includes(SEPARATOR) && event.key === SEPARATOR) {
        event.preventDefault();
      }
    }

    function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
      let newDate = event.target.value;
      const isErase = currentValue ? currentValue.length > newDate.length : false;
      if (!isErase) {
        if (newDate.length >= 2 && !newDate.substr(0, 3).includes(SEPARATOR)) {
          const separatorPosition = newDate.indexOf(SEPARATOR);
          if (separatorPosition >= 0) {
            newDate = newDate.replace(SEPARATOR, '');
          }
          const hour = newDate.substr(0, 2);
          const minute = newDate.substr(2, newDate.length);
          newDate = `${hour}${SEPARATOR}${minute}`;
        }
        if (newDate.length > 5) {
          newDate = newDate.substr(0, 5);
        }
      }
      if (newDate !== currentValue) {
        setCurrentValue(newDate);
      }
    }

    const timeValidators: Validator[] = useMemo(() => {
      const errorMessage = t('common.form.invalid-time');
      const { hour, minute } = parseHourMinute(currentValue);
      return [
        { isValid: typeof hour === 'number' && hour >= 0 && hour < 24, message: errorMessage },
        { isValid: typeof minute === 'number' && minute >= 0 && minute < 60, message: errorMessage },
      ];
    }, [currentValue]);

    const isFormatError = useMemo(() => {
      return !timeValidators.every(v => v.isValid);
    }, [timeValidators]);

    function handleOnBlur() {
      if (!isFormatError) {
        const currentDateAsDayjs = plcDayjs(currentValue, FORMAT);

        if (currentDateAsDayjs.isValid()) {
          setCurrentValue(currentDateAsDayjs.format(FORMAT));
          return onChange(currentDateAsDayjs);
        }
      }
      return onChange(undefined);
    }

    const inputValidators = useMemo(() => {
      const fullValidators = [...timeValidators];
      if (validators) {
        fullValidators.push(...validators);
      }
      return fullValidators;
    }, [timeValidators, validators]);

    return (
      <Input
        ref={timePickerRef}
        inputMode={inputMode}
        isHintVisible={isHintVisible && isFormatError}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        onKeyPress={handleOnKeyPress}
        pattern="^[0-9]{2}:[0-9]{2}$"
        type="text"
        validators={inputValidators}
        value={currentValue}
        {...rest}
      />
    );
  }
);

export { PlcTimePicker };
